import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useT } from "../i18n/I18nProvider";
import { formatMoneyFr } from "../lib/format";

/**
 * City card used on Home and Housing pages.
 *
 * Reads, in order of preference, from:
 *  1. city.numbeo.indicators.{loyers,achat_immobilier} (Numbeo, current truth)
 *  2. city.rent_avg / city.buy_per_m2 (legacy housing_summary seed) — fallback only
 *
 * The fallback prevents the card from breaking if a city has not been
 * imported into Numbeo yet (e.g. reference francophone cities).
 */
function findIndicator(indicators, category, key) {
  if (!indicators) return null;
  const list = indicators[category] || [];
  return list.find((i) => i.key === key) || null;
}

export default function CityCard({ city }) {
  const { t, lp } = useT();
  const slug = city.city_slug || city.slug;
  const name = city.city_name || city.name_fr;

  // Pull Numbeo first (full city object) — fall back to flat housing_summary fields.
  const nb = city.numbeo;
  const rentInd = findIndicator(nb?.indicators, "loyers", "rent_1br_center");
  const buyInd  = findIndicator(nb?.indicators, "achat_immobilier", "buy_price_m2_center");

  const rentDisplay = rentInd?.avg ?? city.rent_avg ?? null;
  const buyDisplay  = buyInd?.avg  ?? city.buy_per_m2 ?? null;

  return (
    <Link to={lp(`/villes/${slug}`)} className="hej-card group block overflow-hidden" data-testid={`city-card-${slug}`}>
      <div className="aspect-[16/10] overflow-hidden bg-slate-100">
        {city.image_url && (
          <img src={city.image_url} alt={name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        )}
      </div>
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display font-semibold text-lg text-slate-900">{name}</h3>
            {rentDisplay !== null ? (
              <p className="text-sm text-slate-500 mt-0.5" data-testid={`city-card-rent-${slug}`}>
                {t("housing.rent1brCenter")} :{" "}
                <span className="font-semibold text-slate-900 tabular-nums">{formatMoneyFr(rentDisplay)}</span>
              </p>
            ) : (
              <p className="text-sm text-slate-500 mt-0.5">{city.region}</p>
            )}
          </div>
          <ArrowUpRight className="w-4 h-4 text-slate-400 group-hover:text-[#0056D2] transition-colors" />
        </div>
        {buyDisplay !== null && (
          <p className="text-xs text-slate-500 mt-2" data-testid={`city-card-buy-${slug}`}>
            {t("housing.buyPerM2")} ~{" "}
            <span className="tabular-nums">{formatMoneyFr(buyDisplay, t("housing.perM2"))}</span>
          </p>
        )}
      </div>
    </Link>
  );
}
